import { useCallback, useRef, useState } from 'react'
import { useEditorRef } from 'platejs/react'
import type { PlateElementProps } from 'platejs/react'
import { clsx } from 'clsx'

import { Flex, CriticalIcon, Heading, Text } from '@buffer-mono/popcorn'
import { InlineCombobox } from '@buffer-mono/popcorn/rich-text'

import { useAutocompleteSuggestions } from '~publish/legacy/editor/plugins/autocomplete'
import { selectAndInsertSuggestion } from '~publish/legacy/editor/plugins/autocomplete/transforms/selectAndInsertSuggestion'
import { AutocompleteSuggestion } from '~publish/legacy/editor/plugins/autocomplete/ui/AutocompleteSuggestion'
import type { BufferEditor } from '~publish/legacy/editor/BufferEditor/types.plate'
import {
  EditorTooltip,
  TooltipButton,
} from '~publish/legacy/editor/components/EditorTooltip'

import { FacebookMentionInputElement } from '../../nodes'

import styles from './FacebookMentionInputElementUI.module.css'

import type { JSX } from 'react'

export const FacebookMentionInputElementUI = (
  props: PlateElementProps<FacebookMentionInputElement>,
): JSX.Element => {
  const { attributes, children, element } = props
  const editor = useEditorRef() as BufferEditor
  const [search, setSearch] = useState(() => element.initialQuery ?? '')
  // InlineCombobox.Content anchors to the input by default, which sits to
  // the right of the (hidden) trigger-character span inside Root — visibly
  // offset from where the user actually typed "@". Root's own node starts
  // at the trigger, so anchor there instead.
  const rootRef = useRef<HTMLSpanElement>(null)

  const { items, error, isLoading } = useAutocompleteSuggestions(
    editor,
    FacebookMentionInputElement,
    search,
    // Once a mention resolves (element.data set), stop fetching entirely.
    !element.data,
  )

  const handleSelect = useCallback(
    (mention: (typeof items)[number]) => {
      selectAndInsertSuggestion(FacebookMentionInputElement)(editor, mention)
    },
    [editor],
  )

  if (element.data) {
    const { url, name } = element.data
    const onOpenClick = (): void => {
      if (url) window.open(url, '_blank', 'noopener,noreferrer')
    }

    return (
      <span {...attributes} className={styles.valid} contentEditable={false}>
        {url ? (
          <EditorTooltip trigger={<span>{name}</span>}>
            <TooltipButton onClick={onOpenClick}>Open Link</TooltipButton>
          </EditorTooltip>
        ) : (
          name
        )}
        {children}
      </span>
    )
  }

  return (
    <span {...attributes}>
      <InlineCombobox.Root
        ref={rootRef}
        trigger={element.trigger}
        value={search}
        onValueChange={setSearch}
      >
        <InlineCombobox.Input aria-label="Search Facebook Pages" />
        <InlineCombobox.Content
          anchorRef={rootRef}
          className={clsx(
            error ? styles.errorPopover : styles.suggestionsPopover,
          )}
        >
          {error ? (
            <InlineCombobox.Status>
              <Flex direction="column" gap="sm" align="center" justify="center">
                <Heading size="small" icon={<CriticalIcon color="critical" />}>
                  Failed to fetch suggestions
                </Heading>
                <Text as="p" align="center">
                  Please try again or refresh your connection. If the problem
                  persists, please contact support.
                </Text>
              </Flex>
            </InlineCombobox.Status>
          ) : (
            <>
              {search.length > 0 && !isLoading && items.length === 0 && (
                <InlineCombobox.Status>No matches found</InlineCombobox.Status>
              )}
              <InlineCombobox.List
                aria-label="Facebook Page results"
                className={styles.list}
              >
                {items.map((mention) => (
                  <InlineCombobox.Item
                    key={mention.key}
                    value={mention.key}
                    textValue={mention.text}
                    onSelect={() => handleSelect(mention)}
                    className={styles.item}
                  >
                    <AutocompleteSuggestion mention={mention} />
                  </InlineCombobox.Item>
                ))}
              </InlineCombobox.List>
            </>
          )}
        </InlineCombobox.Content>
      </InlineCombobox.Root>
      {children}
    </span>
  )
}
